import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

import { AuthService } from '../../auth/auth.service';
import { FormHelper } from '../../form/form-helper';
import { FormHelperFactory } from '../../form/form-helper.factory';
import { phoneNumberValidator } from '../../form/phone-number.validator';
import { Language } from '../../i18n/i18n';
import { OnboardingService } from '../../onboarding/onboarding.service';
import { routes } from '../../routes/routes';

interface Form {
  email: FormControl<string>;
  firstname: FormControl<string>;
  lastname: FormControl<string>;
  phoneNumber: FormControl<string>;
  birthdate: FormControl<string>;
  termsAccepted: FormControl<boolean>;
}

@Component({
  selector: 'app-onboarding-creator-page',
  templateUrl: './onboarding-creator-page.component.html',
  styleUrls: ['./onboarding-creator-page.component.scss'],
})
export class OnboardingCreatorPageComponent {
  form: FormGroup<Form>;
  formHelper: FormHelper<Form>;
  isSaving = false;

  protected readonly routes = routes;

  constructor(
    private onboardingService: OnboardingService,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    formHelperFactory: FormHelperFactory,
  ) {
    const fb = new FormBuilder();
    this.form = fb.group(
      {
        email: fb.nonNullable.control('', [Validators.required, Validators.email]),
        firstname: fb.nonNullable.control('', [Validators.required]),
        lastname: fb.nonNullable.control('', [Validators.required]),
        phoneNumber: fb.nonNullable.control('', [Validators.required, phoneNumberValidator()]),
        birthdate: fb.nonNullable.control('', [Validators.required]),
        termsAccepted: fb.nonNullable.control(false, [Validators.requiredTrue]),
      },
      { updateOn: 'submit' },
    );
    this.formHelper = formHelperFactory.create(this.form);

    this.authService.currentUser$.pipe(take(1)).subscribe(user => {
      if (user) {
        this.form.controls.email.setValue(user.email ?? '');
      }
    });
  }

  async register() {
    this.formHelper.trackSubmitted();

    if (!this.form.valid) {
      return;
    }

    const ref = this.activatedRoute.snapshot.queryParams['ref'];

    try {
      this.isSaving = true;

      await this.onboardingService.completeAsCreator({
        email: this.form.controls.email.value,
        firstname: this.form.controls.firstname.value,
        lastname: this.form.controls.lastname.value,
        birthdate: this.form.controls.birthdate.value,
        phoneNumber: this.form.controls.phoneNumber.value,
        language: this.translateService.currentLang as Language,
        onboardedVia: ref,
      });
    } finally {
      this.isSaving = false;
    }
  }
}
